import {ref, watch} from "vue";
import {
    copiaProfunda,
    generateTextError,
    getPaginate,
    getQueries,
    swal,
    useAPIRest,
    verifyHttpCode,
} from "@/common/constants";
import {listCollectionData} from "@/layouts/main";

/**
 Actualiza los valores de un objeto utilizando otro objeto que contiene las llaves y valores a actualizar.
 @param {Object} objectValue - Objeto que contiene las llaves y valores a actualizar.
 @param {Object} objectToUpdate - Objeto que se va a actualizar. Puede ser una variable reactiva de VueJS (VueRef) o un objeto de JavaScript.
 Si es una variable reactiva de VueJS, se actualizará la propiedad "value".
 @returns {void}
 */
export const updateValues = (objectValue, objectToUpdate) => {
    for (const key in objectValue) {
        if (["RefImpl","qe"].includes(objectToUpdate[key].constructor.name)) {
            if (
                objectToUpdate[key].value.constructor.name !==
                objectValue[key].constructor.name &&
                objectToUpdate[key].value.constructor.name === "Object" &&
                Object.values(objectToUpdate[key].value).length
            ) {
                const copyObjectToUpdate = copiaProfunda(objectToUpdate[key].value);
                copyObjectToUpdate[key] = objectValue[key];
                objectToUpdate[key].value = copyObjectToUpdate;
            } else objectToUpdate[key].value = objectValue[key];
        } else objectToUpdate[key] = objectValue[key];
    }
};
/**
 Guarda o actualiza los datos de una entidad en la base de datos y realiza diferentes acciones dependiendo de los parametros que se pasen.
 @param {Object} props - Contiene todas las variables de los props de un componente de VueJS.
 @param {Function|undefined} emit - Es la función del $emit de un componente de VueJS.
 @param {boolean} [skipSuccessAlert=true] - Indica si se desea omitir la alerta de éxito.
 @param {boolean} [skipErrorAlert=true] - Indica si se desea omitir la alerta de error.
 @param {boolean} [skipAutoList=true] - Indica si se desea omitir la actualización automática de la lista de datos.
 @param {string} [primaryKey='id'] - Es la llave primaria que se utiliza para la actualización de un registro.
 @returns {Promise<Object>} Retorna una promesa que resuelve en un objeto JSON que contiene los datos de la respuesta de la API REST.
 */
export const saveUpdateData = async (props, emit = undefined, skipSuccessAlert = true, skipErrorAlert = true, skipAutoList = true, primaryKey = 'id') => {

    let textoExitoso;
    if (!skipSuccessAlert)
        textoExitoso = `¡${props.entity.gender.toLowerCase() === 'f' ? 'La' : 'El'} ${props.entity.value.toLowerCase()} fue ` + (props.view === 'crear' ? 'creado' : 'actualizado') + ' exitosamente!';
    const callbackCreateUpdate = async (resJSON) => {
        const {statusCode} = resJSON;
        const textoError = generateTextError(resJSON);
        if (verifyHttpCode(statusCode, 'success')) {
            if (!skipSuccessAlert) await swal.fire(textoExitoso, '', 'success');
            if (!skipAutoList) await listCollectionData(props.listApi['list'], emit, props.authToken, props.options?.list?.paginate?.page, props.options?.list?.queries?.orderBy, props.options?.list?.queries?.order, props.options?.list?.queries?.criterion, props.options?.list?.queries?.search, props.options?.list?.queries?.where, props.options?.list?.isPaginate, props.options?.list?.['customQuery']).then((resJSON) => {
                const {statusCode} = resJSON;
                if (verifyHttpCode(statusCode, 'success')) emit('updateValues', {'view': 'listar'});
            });
        } else if (!skipErrorAlert) await swal.fire(textoError, '', 'error');
        return resJSON;
    };
    if ([props.view, props.action].includes('crear')) return await useAPIRest(props.listApi['create'], 'POST', props.authToken, props['loadData'], props.options?.save?.['customQuery'], props.options?.save?.contentType, props.options?.save?.credentials).then(async resJSON => await callbackCreateUpdate(resJSON));
    else return await useAPIRest(props.listApi['update'].replace(':' + primaryKey, props.selectedData[primaryKey]), 'PATCH', props.authToken, props['loadData'], props.options?.update?.['customQuery'], props.options?.update?.contentType, props.options?.update?.credentials).then(async resJSON => await callbackCreateUpdate(resJSON));
};

/**
 Función que se encarga de inicializar y gestionar el estado de la vista principal de la aplicación.
 @async
 @function useLayoutMain
 @param {Object} view - El tipo de vista que se desea renderizar.
 @param {object} collectionData - Objeto reactivo de VueJS que contiene una colección de datos obtenidos al listar los datos de la base de datos.
 @param {string} authToken - Token de autenticación necesario para realizar las operaciones de la base de datos.
 @param {object} selectedData - Objeto reactivo de VueJS que contiene únicamente los valores correspondientes al registro seleccionado.
 @param {object} loadData - Objeto reactivo de VueJS que contiene únicamente los valores a crear y/o actualizar en la base de datos.
 @param {object} listApi - Objeto reactivo de VueJS que contiene la estructura de las API de la lista.
 @param {Object} [customQuery={}] - Los parámetros personalizados que se utilizarán para realizar la solicitud.
 @param {Object} [customQuery.keys] - La clave que se utilizará para filtrar los datos.
 @param {Object} [customQuery.values] - El valor que se utilizará para filtrar los datos.
 @returns {object} Objeto que contiene las funciones necesarias para gestionar el estado de la vista principal.
 */
export const useLayoutMain = async (view, collectionData, authToken, selectedData, loadData, listApi, customQueries = {}) => {
    /*
    Objeto reactivo de VueJS que contiene los valores necesarios para la paginación.
    @name paginate
    @type {object}
    */
    const paginate = ref(getPaginate());
    /**
     Objeto reactivo de VueJS que contiene las consultas a realizar en la base de datos.
     @name queries
     @type {object}
     */
    const queries = ref({...getQueries(), ...customQueries});
    /**
     Objeto que contiene los valores a actualizar en el componente.
     @name valuesToUpdate
     @type {object}
     */
    const valuesToUpdate = {
        'view': view,
        'selectedData': selectedData,
        'collectionData': collectionData,
        'paginate': paginate,
        'queries': queries,
    };
    if (loadData) Object.assign(valuesToUpdate, {
        ...Object.keys(loadData.value).reduce((o, key) => ({
            ...o,
            [key]: loadData
        }), {})
    });
    /**
     Objeto que contiene los valores necesarios para actualizar la lista de datos.
     @name valuesUpdateByList
     @type {object}
     */
    const valuesUpdateByList = {
        'collectionData': valuesToUpdate['collectionData'],
        'paginate': valuesToUpdate['paginate']
    };
    /**
     Función que se encarga de actualizar los valores del registro seleccionado al cambiar algún valor de sus campos.
     @name watchSelectedData
     @function
     @param {object} updatedSelected - Objeto que contiene los valores actualizados del registro seleccionado.
     */
    if(selectedData != undefined){
        watch(selectedData, (updatedSelected) => {
            for (const key of Object.keys(updatedSelected)) {
                if (Object.keys(loadData.value).includes(key))
                    loadData.value[key] = updatedSelected[key];
            }
        });
    }
    /**
     Función que se encarga de actualizar los valores de la lista de datos al cambiar las consultas a realizar.
     @name watchQueries
     @function
     @param {object} newQueries - Objeto que contiene las nuevas consultas a realizar.
     */
    watch(queries, async (newQueries) => await listCollectionData(listApi['list'], valuesUpdateByList, authToken.value, paginate.value.page, newQueries['orderBy'], newQueries['order'], newQueries['criterion'], newQueries['search'], newQueries['where'], undefined, customQueries));

    /**
     Función que escucha cambios en el token de autenticación y actualiza los datos de la colección si se actualiza el token.
     @async
     @function
     @param {object} authToken - Objeto que contiene el token de autenticación.
     @param {function} listCollectionData - Función que lista los datos de la colección.
     @param {object} valuesUpdateByList - Objeto que contiene los valores para actualizar la lista.
     @param {object} customQueries - Objeto que contiene las consultas personalizadas.
     @returns {void}
     */
    watch(authToken, async (tokenUpdated, oldToken) => {
        if (!oldToken) await listCollectionData(listApi['list'], valuesUpdateByList, tokenUpdated, undefined, undefined, undefined, undefined, undefined, undefined, undefined, customQueries);
    });

    if (authToken.value && !collectionData.value.length) await listCollectionData(listApi['list'], valuesUpdateByList, authToken.value, undefined, undefined, undefined, undefined, undefined, undefined, undefined, customQueries);
    /**
     Función que actualiza los valores del objeto con los valores del objeto que se emite.
     @function
     @param {object} objectValueCaseEmit - Objeto que se emite.
     @param {object} objectValueOtherWay - Objeto que se actualiza.
     @returns {void}
     */
    const setupUpdateValues = (objectValueCaseEmit, objectValueOtherWay) => updateValues(objectValueOtherWay ? objectValueOtherWay : objectValueCaseEmit, valuesToUpdate);
    /**
     Función que lista los datos de la colección principal.
     @async
     @function
     @param {number} page - Página actual.
     @param {boolean} isPaginate - Indica si se debe paginar la lista.
     @param {object} customQueries - Objeto que contiene las consultas personalizadas.
     @returns {object} - Objeto que contiene los datos de la colección principal.
     */
    const mainListCollectionData = async (page = 1, isPaginate = true, customQueries = {}) => await listCollectionData(listApi['list'], isPaginate ? valuesUpdateByList : {'collectionData': valuesUpdateByList['collectionData']}, authToken.value, page, queries.value['orderBy'], queries.value['order'], queries.value['criterion'], queries.value['search'], queries.value['where'], isPaginate, customQueries);
    /**
     Limpia los datos seleccionados y para cargar
     @function
     @name cleanLoadSelectedData
     @returns {void}
     */
    const cleanLoadSelectedData = () => {
        setupUpdateValues('updateValues', copiaProfunda(Object.keys(loadData.value).reduce((o, key) => ({
            ...o,
            [key]: ''
        }), {})));
        setupUpdateValues('updateValues', {
            selectedData: copiaProfunda(Object.keys(selectedData.value).reduce((o, key) => ({
                ...o,
                [key]: ''
            }), {}))
        });
    }

    return {paginate, queries, setupUpdateValues, cleanLoadSelectedData, mainListCollectionData};
}

/**
 * Comprueba si el usuario logeado posee un determinado permiso
 * @param {string} permission - el nombre del permiso
 * @returns {Promise<*|boolean>} - verdadero si el usuario logeado tiene el permiso, falso en caso contrario
 */
export const havePermissions = async (permission) => {
    const tokenStored = localStorage.getItem("token")
    const userStored = JSON.parse(localStorage.getItem("user"))
    const permissions = await useAPIRest(process.env.VUE_APP_NEST_AUTH_FINDALLPERMISSIONS.replace(':id', userStored.id), 'GET', tokenStored).then(resJSON => {
        delete resJSON.statusCode;
        return resJSON;
    });
    if (permission === undefined) return true
    return permissions.includes(permission);
}
